/**
 * Rule Parser for Form Runtime
 *
 * Parses string expressions from prm:rule:* attributes into an evaluatable AST.
 *
 * Supported syntax:
 *   field == 'value'
 *   field != 'value'
 *   field > 100
 *   field >= 100
 *   field < 50
 *   field <= 50
 *   field contains 'text'
 *   field empty
 *   field not_empty
 *   (expr) && (expr)
 *   expr || expr
 *
 * This file is designed to be serialized into the Gate iframe runtime.
 */

// ── AST Node Types ──

export interface RuleConditionNode {
    type: "condition";
    field: string;
    operator: RuleOperator;
    value?: string | number | boolean;
}

export interface RuleLogicalNode {
    type: "logical";
    operator: "&&" | "||";
    left: RuleNode;
    right: RuleNode;
}

export interface RuleLiteralNode {
    type: "literal";
    value: boolean;
}

export interface RuleErrorNode {
    type: "error";
    message: string;
    raw: string;
}

export type RuleNode = RuleConditionNode | RuleLogicalNode | RuleLiteralNode | RuleErrorNode;

export type RuleOperator =
    | "=="
    | "!="
    | ">"
    | "<"
    | ">="
    | "<="
    | "contains"
    | "empty"
    | "not_empty";

const UNARY_OPERATORS = new Set<RuleOperator>(["empty", "not_empty"]);
const BINARY_OPERATORS = new Set<RuleOperator>(["==", "!=", ">", "<", ">=", "<=", "contains"]);

// ── Tokenizer ──

interface Token {
    type: "field" | "operator" | "value" | "logical" | "paren_open" | "paren_close";
    value: string;
}

const tokenize = (input: string): Token[] => {
    const tokens: Token[] = [];
    let i = 0;
    const len = input.length;

    while (i < len) {
        // Skip whitespace
        if (/\s/.test(input[i])) {
            i++;
            continue;
        }

        // Parentheses
        if (input[i] === "(") {
            tokens.push({ type: "paren_open", value: "(" });
            i++;
            continue;
        }
        if (input[i] === ")") {
            tokens.push({ type: "paren_close", value: ")" });
            i++;
            continue;
        }

        // Logical operators
        if (input[i] === "&" && input[i + 1] === "&") {
            tokens.push({ type: "logical", value: "&&" });
            i += 2;
            continue;
        }
        if (input[i] === "|" && input[i + 1] === "|") {
            tokens.push({ type: "logical", value: "||" });
            i += 2;
            continue;
        }

        // Comparison operators (multi-char first)
        if (input[i] === "!" && input[i + 1] === "=") {
            tokens.push({ type: "operator", value: "!=" });
            i += 2;
            continue;
        }
        if (input[i] === "=" && input[i + 1] === "=") {
            tokens.push({ type: "operator", value: "==" });
            i += 2;
            continue;
        }
        if (input[i] === ">" && input[i + 1] === "=") {
            tokens.push({ type: "operator", value: ">=" });
            i += 2;
            continue;
        }
        if (input[i] === "<" && input[i + 1] === "=") {
            tokens.push({ type: "operator", value: "<=" });
            i += 2;
            continue;
        }
        if (input[i] === ">") {
            tokens.push({ type: "operator", value: ">" });
            i++;
            continue;
        }
        if (input[i] === "<") {
            tokens.push({ type: "operator", value: "<" });
            i++;
            continue;
        }

        // Quoted string value
        if (input[i] === "'" || input[i] === '"') {
            const quote = input[i];
            let str = "";
            i++;
            while (i < len && input[i] !== quote) {
                if (input[i] === "\\" && i + 1 < len) {
                    str += input[i + 1];
                    i += 2;
                } else {
                    str += input[i];
                    i++;
                }
            }
            i++; // skip closing quote
            tokens.push({ type: "value", value: str });
            continue;
        }

        // Word: could be a field name, keyword operator, or unquoted value
        let word = "";
        while (i < len && /[a-zA-Z0-9_.\-]/.test(input[i])) {
            word += input[i];
            i++;
        }

        if (word) {
            // Check if it's a keyword operator
            if (word === "contains" || word === "empty" || word === "not_empty") {
                tokens.push({ type: "operator", value: word });
            } else if (word === "true") {
                tokens.push({ type: "value", value: "true" });
            } else if (word === "false") {
                tokens.push({ type: "value", value: "false" });
            } else if (/^\d+(\.\d+)?$/.test(word)) {
                tokens.push({ type: "value", value: word });
            } else {
                tokens.push({ type: "field", value: word });
            }
            continue;
        }

        // Unknown character, skip
        i++;
    }

    return tokens;
};

// ── Parser ──

class RuleParserEngine {
    private tokens: Token[];
    private pos: number;

    constructor(tokens: Token[]) {
        this.tokens = tokens;
        this.pos = 0;
    }

    private peek(): Token | undefined {
        return this.tokens[this.pos];
    }

    private consume(): Token | undefined {
        return this.tokens[this.pos++];
    }

    private parseValue(token: Token): string | number | boolean {
        const raw = token.value;
        if (raw === "true") return true;
        if (raw === "false") return false;
        const num = Number(raw);
        if (!isNaN(num) && raw !== "") return num;
        return raw;
    }

    parseExpression(): RuleNode {
        let left = this.parsePrimary();

        while (this.peek()?.type === "logical") {
            const logicalToken = this.consume()!;
            const right = this.parsePrimary();
            left = {
                type: "logical",
                operator: logicalToken.value as "&&" | "||",
                left,
                right,
            };
        }

        return left;
    }

    private parsePrimary(): RuleNode {
        const token = this.peek();

        if (!token) {
            return { type: "literal", value: true };
        }

        // Parenthesized expression
        if (token.type === "paren_open") {
            this.consume(); // (
            const expr = this.parseExpression();
            if (this.peek()?.type === "paren_close") {
                this.consume(); // )
            }
            return expr;
        }

        // Literal boolean value
        if (token.type === "value") {
            this.consume();
            const val = this.parseValue(token);
            if (typeof val === "boolean") {
                return { type: "literal", value: val };
            }
            return { type: "literal", value: Boolean(val) };
        }

        // Condition: field operator [value]
        if (token.type === "field") {
            this.consume();
            const field = token.value;

            const opToken = this.peek();
            if (!opToken || opToken.type !== "operator") {
                // Just a field name without operator — treat as "not_empty"
                return { type: "condition", field, operator: "not_empty" };
            }

            this.consume();
            const operator = opToken.value as RuleOperator;

            if (UNARY_OPERATORS.has(operator)) {
                return { type: "condition", field, operator };
            }

            if (BINARY_OPERATORS.has(operator)) {
                const valueToken = this.peek();
                if (valueToken && (valueToken.type === "value" || valueToken.type === "field")) {
                    this.consume();
                    const value = valueToken.type === "field" ? valueToken.value : this.parseValue(valueToken);
                    return { type: "condition", field, operator, value };
                }
                return { type: "condition", field, operator, value: "" };
            }

            return { type: "condition", field, operator: "==" as RuleOperator, value: operator };
        }

        // Fallback
        this.consume();
        return { type: "literal", value: true };
    }
}

// ── Public API ──

/**
 * Parse a rule expression string into an AST node.
 *
 * @example
 * parseRule("category == 'incident'")
 * // → { type: "condition", field: "category", operator: "==", value: "incident" }
 *
 * parseRule("category == 'incident' && priority == 'high'")
 * // → { type: "logical", operator: "&&", left: {...}, right: {...} }
 *
 * parseRule("description empty")
 * // → { type: "condition", field: "description", operator: "empty" }
 */
export const parseRule = (expression: string): RuleNode => {
    const trimmed = (expression || "").trim();

    if (!trimmed) {
        return { type: "literal", value: true };
    }

    if (trimmed === "true") {
        return { type: "literal", value: true };
    }

    if (trimmed === "false") {
        return { type: "literal", value: false };
    }

    try {
        const tokens = tokenize(trimmed);
        if (tokens.length === 0) {
            return { type: "literal", value: true };
        }

        const parser = new RuleParserEngine(tokens);
        return parser.parseExpression();
    } catch (error) {
        return {
            type: "error",
            message: error instanceof Error ? error.message : String(error),
            raw: trimmed,
        };
    }
};

/**
 * Serialize the rule parser into a self-contained JavaScript string
 * for embedding in the Gate iframe runtime.
 */
export const serializeRuleParserForRuntime = (): string => {
    return `
(function() {
    const UNARY_OPS = new Set(["empty", "not_empty"]);
    const BINARY_OPS = new Set(["==", "!=", ">", "<", ">=", "<=", "contains"]);

    function tokenize(input) {
        var tokens = [];
        var i = 0;
        var len = input.length;
        while (i < len) {
            if (/\\s/.test(input[i])) { i++; continue; }
            if (input[i] === "(") { tokens.push({ t: "po", v: "(" }); i++; continue; }
            if (input[i] === ")") { tokens.push({ t: "pc", v: ")" }); i++; continue; }
            if (input[i] === "&" && input[i+1] === "&") { tokens.push({ t: "l", v: "&&" }); i += 2; continue; }
            if (input[i] === "|" && input[i+1] === "|") { tokens.push({ t: "l", v: "||" }); i += 2; continue; }
            if (input[i] === "!" && input[i+1] === "=") { tokens.push({ t: "o", v: "!=" }); i += 2; continue; }
            if (input[i] === "=" && input[i+1] === "=") { tokens.push({ t: "o", v: "==" }); i += 2; continue; }
            if (input[i] === ">" && input[i+1] === "=") { tokens.push({ t: "o", v: ">=" }); i += 2; continue; }
            if (input[i] === "<" && input[i+1] === "=") { tokens.push({ t: "o", v: "<=" }); i += 2; continue; }
            if (input[i] === ">") { tokens.push({ t: "o", v: ">" }); i++; continue; }
            if (input[i] === "<") { tokens.push({ t: "o", v: "<" }); i++; continue; }
            if (input[i] === "'" || input[i] === '"') {
                var q = input[i], s = ""; i++;
                while (i < len && input[i] !== q) { if (input[i] === "\\\\" && i+1 < len) { s += input[i+1]; i += 2; } else { s += input[i]; i++; } }
                i++; tokens.push({ t: "v", v: s }); continue;
            }
            var w = "";
            while (i < len && /[a-zA-Z0-9_.\\-]/.test(input[i])) { w += input[i]; i++; }
            if (w) {
                if (w === "contains" || w === "empty" || w === "not_empty") { tokens.push({ t: "o", v: w }); }
                else if (w === "true" || w === "false") { tokens.push({ t: "v", v: w }); }
                else if (/^\\d+(\\.\\d+)?$/.test(w)) { tokens.push({ t: "v", v: w }); }
                else { tokens.push({ t: "f", v: w }); }
                continue;
            }
            i++;
        }
        return tokens;
    }

    function pv(tok) {
        var r = tok.v;
        if (r === "true") return true;
        if (r === "false") return false;
        var n = Number(r);
        if (!isNaN(n) && r !== "") return n;
        return r;
    }

    function parseExpr(tokens, pos) {
        var result = parsePrimary(tokens, pos);
        var node = result[0]; pos = result[1];
        while (pos < tokens.length && tokens[pos].t === "l") {
            var op = tokens[pos].v; pos++;
            var rr = parsePrimary(tokens, pos);
            node = { t: "l", o: op, a: node, b: rr[0] };
            pos = rr[1];
        }
        return [node, pos];
    }

    function parsePrimary(tokens, pos) {
        if (pos >= tokens.length) return [{ t: "b", v: true }, pos];
        var tok = tokens[pos];
        if (tok.t === "po") {
            pos++;
            var result = parseExpr(tokens, pos);
            pos = result[1];
            if (pos < tokens.length && tokens[pos].t === "pc") pos++;
            return [result[0], pos];
        }
        if (tok.t === "v") {
            pos++;
            var val = pv(tok);
            return [{ t: "b", v: typeof val === "boolean" ? val : Boolean(val) }, pos];
        }
        if (tok.t === "f") {
            pos++;
            var field = tok.v;
            if (pos >= tokens.length || tokens[pos].t !== "o") {
                return [{ t: "c", f: field, o: "not_empty" }, pos];
            }
            var opTok = tokens[pos]; pos++;
            var operator = opTok.v;
            if (UNARY_OPS.has(operator)) {
                return [{ t: "c", f: field, o: operator }, pos];
            }
            if (BINARY_OPS.has(operator)) {
                if (pos < tokens.length && (tokens[pos].t === "v" || tokens[pos].t === "f")) {
                    var valTok = tokens[pos]; pos++;
                    var value = valTok.t === "f" ? valTok.v : pv(valTok);
                    return [{ t: "c", f: field, o: operator, v: value }, pos];
                }
                return [{ t: "c", f: field, o: operator, v: "" }, pos];
            }
            return [{ t: "c", f: field, o: "==", v: operator }, pos];
        }
        pos++;
        return [{ t: "b", v: true }, pos];
    }

    window.__prmParseRule = function(expr) {
        var trimmed = (expr || "").trim();
        if (!trimmed) return { t: "b", v: true };
        if (trimmed === "true") return { t: "b", v: true };
        if (trimmed === "false") return { t: "b", v: false };
        try {
            var tokens = tokenize(trimmed);
            if (tokens.length === 0) return { t: "b", v: true };
            var result = parseExpr(tokens, 0);
            return result[0];
        } catch (e) {
            return { t: "e", m: String(e), r: trimmed };
        }
    };
})();
`;
};
